import React, { Fragment, useMemo, useState, useEffect, useRef } from "react";
import classnames from "classnames";
import {
  useTable,
  useFilters,
  useGlobalFilter,
  useSortBy,
  usePagination,
  useRowSelect,
  Column,
  Filters,
  FilterTypes,
} from "react-table";
import {
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Filter,
} from "react-feather";

import Loader from "../Loader";

export interface DataTableProps<T extends object> {
  columns: Column<T>[];
  data: T[];
  manualFilters?: boolean;
  disabled?: (item: T) => boolean;
  selected?: (item: T) => boolean;
  onSelect?: (item: T) => void;
  filters?: Filters<T>;
  onChangeFilter?: (filters: Filters<T>, items: T[]) => void;
  isLoading?: boolean;
  isFiltering?: boolean;
  messages?: {
    noRecords?: string;
    noMatches?: string;
  };
}

const DataTable = <T extends object>(props: DataTableProps<T>) => {

  const columns = useMemo(() => props.columns, [props.columns]);
  const data = useMemo(() => props.data, [props.data]);
  const filterRef = useRef<HTMLDivElement>(null);

  const [filterName, setFilterColumnName] = useState<string>();

  const closeFilterClickHandler = (e: Event) => {
    if (filterRef.current) {
      if (
        filterRef.current &&
        filterRef.current !== e.target &&
        !filterRef.current.contains(e.target as any)
      ) {
        closeFilter();
      }
    } else {
      // Ref does not exist - component has probably been unmounted
      window.removeEventListener("click", closeFilterClickHandler);
    }
  };

  const openFilter = (name: string) => {
    // Timeout ensures the click event has ended
    window.setTimeout(() => {
      setFilterColumnName(name);
      window.addEventListener("click", closeFilterClickHandler);
    });
  };

  const closeFilter = () => {
    window.removeEventListener("click", closeFilterClickHandler);
    setFilterColumnName(void 0);
  };

  const onSelect = (item: T) => {
    if (props.onSelect && (!props.disabled || !props.disabled(item))) {
      props.onSelect(item);
    }
  };

  const getClassNames = (item: T) => {
    const obj: { [key: string]: boolean } = {};
    if (props.disabled && props.disabled(item)) {
      obj["disabled"] = true;
    }
    if (props.selected && props.selected(item)) {
      obj["selectedRow"] = true;
    }
    return classnames(obj);
  }

  const filterTypes = useMemo<FilterTypes<T>>(() => ({
    multiSelect: (rows, id, filterValues) => {
      console.info(id);
      console.table(filterValues);
      console.table(rows);
      const key = id[0];
      if (rows && filterValues && filterValues.length !== 0) {
        console.table(rows[0].values);
        return rows.filter(row => filterValues.includes(row.values[key]));
      }
      return rows;
    },
  }), []);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    rows,
    preFilteredRows,
    filteredRows,
    prepareRow,
    state,
    // The rest of these things are super handy, too ;)
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    flatRows,
  } = useTable(
    {
      columns,
      data,
      //defaultColumn,
      filterTypes,
      initialState: {
        pageSize: 20,
        pageIndex: 0,
        filters: props.filters ?? [],
      },
      manualFilters: props.manualFilters ?? false,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
  );

  const { filters, pageIndex, pageSize } = state;

  const anyRecords = useMemo(
    () => !!preFilteredRows?.length,
    [preFilteredRows]);

  const anyFilteredRecords = useMemo(
    () => !!filteredRows?.length,
    [filteredRows]);

  const isFiltered = useMemo(
    () => !!filters && !!filters.length,
    [filters]);

  useEffect(() => {
    if (props.onChangeFilter) {
      props.onChangeFilter(filters, rows.map(row => row.original));
    }
  }, [filters]);

  return (
    <>
      {props.isLoading
        ? (
          <div className="position-relative" style={{ minHeight: "10rem" }}>
            <Loader show background="none" />
          </div>
        )
        : !anyRecords && !isFiltered && !props.isFiltering
          ? (
            <div className="text-center my-5">
              {props.messages?.noRecords ?? "No records found"}
            </div>
          )
          : (
            <div className={classnames({
              "table-responsive": true,
              "table-responsive-selectable": !!props.onSelect,
            })}>
              <table {...getTableProps()} className="table">
                <thead>
                  {headerGroups.map((headerGroup, i) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column, i) => (
                        <th key={i} style={{ width: column.width, verticalAlign: "middle" }}>
                          <div className="d-flex align-items-center">
                            <span
                              className="d-flex align-items-center"
                              {...column.getHeaderProps(
                                column.canSort ? column.getSortByToggleProps() : void 0
                              )}
                            >
                              <span>
                                {column.render("Header")}
                              </span>
                              {/* Sort icon */}
                              {column.canSort && (
                                <span className="ml-1">
                                  {column.isSorted ? (
                                    column.isSortedDesc ? (
                                      <ChevronDown />
                                    ) : (
                                      <ChevronUp />
                                    )
                                  ) : (
                                    <ChevronDown style={{ opacity: 0.4 }} />
                                  )}
                                </span>
                              )}
                            </span>
                            {/* Filter icon */}

                            {column.canFilter && column.Filter ? (
                              <Fragment>
                                {column.canFilter && !!column.Filter && (
                                  <span
                                    className="ml-2"
                                    onClick={(e) => {
                                      if (column.id !== filterName) {
                                        openFilter(column.id);
                                      }
                                    }}
                                  >
                                    <Filter
                                      style={{ cursor: "pointer" }}
                                      fill={
                                        filters.find((f) => f.id == column.id)
                                          ? "white"
                                          : "none"
                                      }
                                    />
                                  </span>
                                )}
                              </Fragment>
                            ) : null}
                          </div>
                          {/* Filter box */}
                          {column.id === filterName && (
                            <div className={`form filterComponent ${column.filter ?? "text"}`} ref={filterRef}>
                              {column.canFilter && column.render("Filter")}
                            </div>
                          )}
                        </th>
                      )
                      )}
                    </tr>
                  ))}
                </thead>
                {anyFilteredRecords && (
                  <tbody className="bg-secondary" style={props.isFiltering ? { opacity: 0.5 } : void 0} {...getTableBodyProps()}>
                    {page.map(row => {
                      prepareRow(row);
                      return (
                        <tr
                          onClick={() => onSelect(row.original)}
                          className={getClassNames(row.original)}
                          {...row.getRowProps()}
                        >
                          {row.cells.map((cell) => {
                            return (
                              <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                )}
              </table>
              {!anyFilteredRecords && (isFiltered || props.isFiltering) && (
                <div className="text-center my-5" style={props.isFiltering ? { opacity: 0.5 } : void 0}>
                  {props.messages?.noMatches ?? "No matching records found"}
                </div>
              )}
              {/* 
        Pagination can be built however you'd like. 
        This is just a very basic UI implementation:
      */}
              {!!flatRows.length && (
                <div className="d-flex align-items-center justify-content-between">
                  <span>
                    {/* First */}
                    <button
                      className="btn btn-sm btn-link mr-1"
                      onClick={() => gotoPage(0)}
                      disabled={!canPreviousPage}
                    >
                      <ChevronsLeft />
                    </button>
                    {/* Previous */}
                    <button
                      className="btn btn-sm btn-link"
                      onClick={() => previousPage()}
                      disabled={!canPreviousPage}
                    >
                      <ChevronLeft />
                    </button>
                  </span>
                  {/* X of Y */}
                  <span className="mx-2">
                    <strong>
                      {(pageIndex * pageSize + 1).toLocaleString()} -{" "}
                      {(Math.min((pageIndex + 1) * pageSize, flatRows.length)).toLocaleString()} of{" "}
                      {flatRows.length.toLocaleString()}
                    </strong>
                  </span>
                  {/* <span>
          | Go to page:{" "}
          <input
            type="number"
            defaultValue={pageIndex + 1}
            onChange={(e) => {
              const page = e.target.value ? Number(e.target.value) - 1 : 0;
              gotoPage(page);
            }}
            style={{ width: "100px" }}
          />
        </span>{" "} */}
                  {/*<select*/}
                  {/*  value={pageSize}*/}
                  {/*  onChange={(e) => {*/}
                  {/*    setPageSize(Number(e.target.value));*/}
                  {/*  }}*/}
                  {/*>*/}
                  {/*  {[10, 20, 30, 40, 50].map((pageSize) => (*/}
                  {/*    <option key={pageSize} value={pageSize}>*/}
                  {/*      Show {pageSize}*/}
                  {/*    </option>*/}
                  {/*  ))}*/}
                  {/*</select>*/}
                  <span>
                    {/* Next */}
                    <button
                      className="btn btn-sm btn-link"
                      onClick={() => nextPage()}
                      disabled={!canNextPage}
                    >
                      <ChevronRight />
                    </button>
                    {/* Last */}
                    <button
                      className="btn btn-sm btn-link ml-1"
                      onClick={() => gotoPage(pageCount - 1)}
                      disabled={!canNextPage}
                    >
                      <ChevronsRight />
                    </button>
                  </span>
                </div>
              )}
            </div>
          )}
    </>
  );
};

export default DataTable;
